// Tour — orchestrator. Given an active tour definition, walks the user
// through the step list with a spotlight + popover, supporting Esc to skip,
// arrow keys / Enter to navigate, and a callback when the user finishes or
// skips.
//
// "Click" mode only — autoplay/showcase modes from the design handoff are
// demo-only and intentionally not ported.

import { useCallback, useEffect, useState } from 'react';
import Popover from './Popover';
import Spotlight from './Spotlight';
import useAnchorRect from './useAnchorRect';
import type { TourDefinition } from './types';

export type TourProps = {
  tour: TourDefinition;
  /** Called when the user reaches the end via "Done". */
  onComplete: () => void;
  /** Called when the user dismisses early (Skip, Esc, or close). */
  onSkip: () => void;
};

export default function Tour({ tour, onComplete, onSkip }: TourProps) {
  const [index, setIndex] = useState(0);
  const step = tour.steps[index];
  const rect = useAnchorRect(step?.anchor ?? null);

  const total = tour.steps.length;
  const isLast = index === total - 1;

  const goNext = useCallback(() => {
    if (isLast) {
      onComplete();
    } else {
      setIndex((i) => i + 1);
    }
  }, [isLast, onComplete]);

  const goBack = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  // Keyboard nav: Esc skips, ArrowRight / Enter advances, ArrowLeft goes back.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onSkip();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        goNext();
        return;
      }
      if (e.key === 'ArrowLeft') {
        goBack();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goBack, onSkip]);

  if (!step) return null;

  return (
    <>
      <Spotlight rect={rect} />
      <Popover
        rect={rect}
        title={step.title}
        text={step.text}
        placement={step.placement}
        index={index}
        total={total}
        onBack={goBack}
        onNext={goNext}
        onSkip={onSkip}
      />
    </>
  );
}
